import React, { useState } from 'react';

interface CompletionStepProps {
  data: any;
  onComplete: () => void;
  onPrevious: () => void;
}

const CompletionStep: React.FC<CompletionStepProps> = ({ data, onComplete, onPrevious }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleComplete = async () => {
    setIsLoading(true);
    
    try {
      await onComplete();
    } catch (error) {
      console.error('Error completing onboarding:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatAddress = (address: any) => {
    if (!address) return 'Not provided';
    return `${address.street}, ${address.city}, ${address.state} ${address.zipCode}`;
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Application</h2>
        <p className="text-gray-600">
          Please review all the information you've provided. Once you submit, your application will be sent to 242Network staff for review.
        </p>
      </div>

      <div className="space-y-8">
        {/* Profile Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Name</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.profile?.firstName} {data.profile?.lastName}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Email</dt>
              <dd className="mt-1 text-sm text-gray-900">{data.profile?.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Phone</dt>
              <dd className="mt-1 text-sm text-gray-900">{data.profile?.phone}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Marital Status</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">{data.profile?.maritalStatus}</dd>
            </div>
            {data.profile?.maritalStatus === 'married' && (
              <>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Spouse Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{data.profile?.spouseName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Spouse Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{data.profile?.spouseEmail}</dd>
                </div>
              </>
            )}
            <div>
              <dt className="text-sm font-medium text-gray-500">Address</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatAddress(data.profile?.address)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Target Location</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.profile?.targetCity}, {data.profile?.targetState}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Proposed Church Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{data.profile?.churchName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Planting Timeline</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {data.profile?.plantingTimeline?.replace('_', ' ')}
              </dd>
            </div>
          </div>
        </div>

        {/* Theology Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Theological Survey</h3>
          <div className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Scripture Affirmation</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.theology?.scriptureAffirmation ? (
                  <span className="text-green-600">✓ Completed</span>
                ) : (
                  <span className="text-red-600">✗ Not completed</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Trinity Understanding</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.theology?.trinityUnderstanding ? (
                  <span className="text-green-600">✓ Completed</span>
                ) : (
                  <span className="text-red-600">✗ Not completed</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Salvation Doctrines</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.theology?.salvationDoctrines ? (
                  <span className="text-green-600">✓ Completed</span>
                ) : (
                  <span className="text-red-600">✗ Not completed</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Baptist Distinctives</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.theology?.believersBaptism ? (
                  <span className="text-green-600">✓ Completed</span>
                ) : (
                  <span className="text-red-600">✗ Not completed</span>
                )}
              </dd>
            </div>
          </div>
        </div>

        {/* References Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">References</h3>
          <div className="space-y-4">
            {data.references?.map((ref: any, index: number) => (
              <div key={index} className="border-l-4 border-primary-200 pl-4">
                <div className="flex items-center justify-between">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Reference {index + 1}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {ref.name} ({ref.relationshipType})
                    </dd>
                  </div>
                  <div className="text-right">
                    <dd className="text-sm text-gray-900">{ref.email}</dd>
                    {ref.organization && (
                      <dd className="text-xs text-gray-500">{ref.organization}</dd>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sending Church Summary */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Sending Church</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500">Church Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{data.sendingChurch?.churchName}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Denomination</dt>
              <dd className="mt-1 text-sm text-gray-900">{data.sendingChurch?.denomination || 'Not specified'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Primary Contact</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.sendingChurch?.primaryContactName} ({data.sendingChurch?.primaryContactTitle})
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Membership Status</dt>
              <dd className="mt-1 text-sm text-gray-900 capitalize">
                {data.sendingChurch?.membershipStatus?.replace('_', ' ')}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Financial Support</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.sendingChurch?.financialSupport ? (
                  <span className="text-green-600">Yes</span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Prayer Support</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.sendingChurch?.prayerSupport ? (
                  <span className="text-green-600">Yes</span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Endorsement</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {data.sendingChurch?.endorsementProvided ? (
                  <span className="text-green-600">Yes</span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </dd>
            </div>
          </div>
        </div>

        {/* Spiritual Health Status */}
        <div className="bg-teal-50 rounded-lg p-6 border-2 border-teal-200">
          <h3 className="text-lg font-medium text-teal-900 mb-4">Spiritual & Emotional Health Assessment</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <dt className="text-sm font-medium text-gray-600 mb-2">Candidate Survey</dt>
              <dd>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  data.spiritualHealth?.spiritualHealthCandidateSubmitted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {data.spiritualHealth?.spiritualHealthCandidateSubmitted ? '✓ Completed' : '○ Not Completed'}
                </span>
                {data.spiritualHealth?.candidateSurvey?.submittedAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    Submitted: {new Date(data.spiritualHealth.candidateSurvey.submittedAt).toLocaleDateString()}
                  </p>
                )}
              </dd>
            </div>
            {data.profile?.maritalStatus === 'married' && (
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <dt className="text-sm font-medium text-gray-600 mb-2">Spouse Survey</dt>
                <dd>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    data.spiritualHealth?.spiritualHealthSpouseSubmitted
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {data.spiritualHealth?.spiritualHealthSpouseSubmitted ? '✓ Completed' : '○ Not Completed'}
                  </span>
                  {data.spiritualHealth?.spouseSurvey?.submittedAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      Submitted: {new Date(data.spiritualHealth.spouseSurvey.submittedAt).toLocaleDateString()}
                    </p>
                  )}
                </dd>
              </div>
            )}
          </div>
          {data.profile?.maritalStatus === 'married' && 
           (!data.spiritualHealth?.spiritualHealthCandidateSubmitted || !data.spiritualHealth?.spiritualHealthSpouseSubmitted) && (
            <div className="mt-4 p-3 bg-teal-100 rounded-lg">
              <p className="text-sm text-teal-800">
                <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Both you and your spouse need to complete the spiritual health assessment.
              </p>
            </div>
          )}
          {data.profile?.maritalStatus !== 'married' && !data.spiritualHealth?.spiritualHealthCandidateSubmitted && (
            <div className="mt-4 p-3 bg-teal-100 rounded-lg">
              <p className="text-sm text-teal-800">
                <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Please complete the spiritual health assessment.
              </p>
            </div>
          )}
        </div>

        {/* Background Check Status */}
        <div className="bg-blue-50 rounded-lg p-6 border-2 border-blue-200">
          <h3 className="text-lg font-medium text-blue-900 mb-4">Background Check Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <dt className="text-sm font-medium text-gray-600 mb-2">Your Submission</dt>
              <dd>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  data.backgroundCheck?.submitted
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {data.backgroundCheck?.submitted ? '✓ Submitted' : '○ Not Submitted'}
                </span>
                {data.backgroundCheck?.submittedAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    Submitted: {new Date(data.backgroundCheck.submittedAt).toLocaleDateString()}
                  </p>
                )}
              </dd>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200">
              <dt className="text-sm font-medium text-gray-600 mb-2">Admin Processing Status</dt>
              <dd>
                <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                  data.profile?.backgroundCheckStatus === 'received' ? 'bg-green-100 text-green-800' :
                  data.profile?.backgroundCheckStatus === 'submitted' ? 'bg-blue-100 text-blue-800' :
                  data.profile?.backgroundCheckStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {data.profile?.backgroundCheckStatus === 'received' && '✓ Complete'}
                  {data.profile?.backgroundCheckStatus === 'submitted' && '◐ Processing'}
                  {data.profile?.backgroundCheckStatus === 'started' && '○ Started'}
                  {!data.profile?.backgroundCheckStatus && '○ Not Started'}
                </span>
                {data.profile?.backgroundCheckStatusUpdatedAt && (
                  <p className="text-xs text-gray-500 mt-2">
                    Updated: {new Date(data.profile.backgroundCheckStatusUpdatedAt).toLocaleDateString()}
                  </p>
                )}
              </dd>
            </div>
          </div>
          {data.profile?.backgroundCheckNotes && (
            <div className="mt-4 p-3 bg-white rounded border border-blue-200">
              <p className="text-xs font-medium text-gray-600 mb-1">Coordinator Notes:</p>
              <p className="text-sm text-gray-700">{data.profile.backgroundCheckNotes}</p>
            </div>
          )}
          {data.profile?.backgroundCheckStatus !== 'received' && (
            <div className="mt-4 p-3 bg-blue-100 rounded-lg">
              <p className="text-sm text-blue-800">
                <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {data.backgroundCheck?.submitted 
                  ? 'Your background check is being processed. This typically takes 7-10 business days.'
                  : 'Please complete the background check step to continue.'}
              </p>
            </div>
          )}
        </div>

        {/* Prepare/Enrich Status (if married) */}
        {data.profile?.maritalStatus === 'married' && (
          <div className="bg-purple-50 rounded-lg p-6 border-2 border-purple-200">
            <h3 className="text-lg font-medium text-purple-900 mb-4">Prepare/Enrich Counseling Status</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <dt className="text-sm font-medium text-gray-600 mb-2">Your Request</dt>
                <dd>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    data.prepareEnrich?.requested
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {data.prepareEnrich?.requested ? '✓ Requested' : '○ Not Requested'}
                  </span>
                  {data.prepareEnrich?.requestedAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      Requested: {new Date(data.prepareEnrich.requestedAt).toLocaleDateString()}
                    </p>
                  )}
                </dd>
              </div>
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <dt className="text-sm font-medium text-gray-600 mb-2">Counselor Status</dt>
                <dd>
                  <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                    data.profile?.prepareEnrichStatus === 'complete' ? 'bg-green-100 text-green-800' :
                    data.profile?.prepareEnrichStatus === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                    data.profile?.prepareEnrichStatus === 'started' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {data.profile?.prepareEnrichStatus === 'complete' && '✓ Complete'}
                    {data.profile?.prepareEnrichStatus === 'scheduled' && '📅 Scheduled'}
                    {data.profile?.prepareEnrichStatus === 'started' && '◐ In Progress'}
                    {!data.profile?.prepareEnrichStatus && '○ Not Started'}
                  </span>
                  {data.profile?.prepareEnrichStatusUpdatedAt && (
                    <p className="text-xs text-gray-500 mt-2">
                      Updated: {new Date(data.profile.prepareEnrichStatusUpdatedAt).toLocaleDateString()}
                    </p>
                  )}
                </dd>
              </div>
            </div>
            {data.profile?.prepareEnrichNotes && (
              <div className="mt-4 p-3 bg-white rounded border border-purple-200">
                <p className="text-xs font-medium text-gray-600 mb-1">Counselor Notes:</p>
                <p className="text-sm text-gray-700">{data.profile.prepareEnrichNotes}</p>
              </div>
            )}
            {data.profile?.prepareEnrichStatus !== 'complete' && (
              <div className="mt-4 p-3 bg-purple-100 rounded-lg">
                <p className="text-sm text-purple-800">
                  <svg className="inline h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {data.prepareEnrich?.requested 
                    ? 'A counselor will contact you to schedule your Prepare/Enrich sessions. This typically takes 4-6 weeks to complete.'
                    : 'Please request counseling in the Prepare/Enrich step to continue.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-blue-900 mb-4">What Happens Next?</h3>
          <div className="space-y-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  1
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Application Review:</strong> 242Network staff will review your application and theological survey.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  2
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Reference Contact:</strong> We will contact your references to gather additional information.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  3
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Sending Church Verification:</strong> We will verify the information with your sending church.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  4
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Background Check & Counseling:</strong> Complete all background check and counseling requirements.
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 text-sm font-medium">
                  5
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Final Review:</strong> You will be contacted within 1-2 weeks with next steps in the process.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Questions or Need Help?</h3>
          <p className="text-sm text-gray-600">
            If you have any questions about your application or need assistance, please reach out to your 242 Network hub leader.
          </p>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-between pt-6 border-t border-gray-200 mt-8">
        <button
          type="button"
          onClick={onPrevious}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous Step
        </button>

        <button
          type="button"
          onClick={handleComplete}
          disabled={isLoading}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting Application...
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Submit Application
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default CompletionStep;
